import React, { useCallback, useEffect, useRef, useState } from "react";
import BattlefieldStack from "./BattlefieldStack";
import FrameActionPanel from "./FrameActionPanel";
import { BATTLE_PROJECTION_MODES } from "./battleProjection";

const clampPercent = (value, min = 1, max = 99) => Math.max(min, Math.min(max, Number(value || 0)));

const resolveOfficerSourceMarkers = (attackEvents = [], skillCatalogById = {}) => {
  const markers = [];
  const seen = new Set();
  (Array.isArray(attackEvents) ? attackEvents : []).forEach((event, index) => {
    const marker = event?.sourceMarker;
    if (!marker || marker.type !== "officer") return;
    const skillId = Math.trunc(Number(marker?.skillId || event?.sourceSkillId));
    if (!Number.isFinite(skillId) || skillId <= 0) return;
    const catalogEntry = skillCatalogById[String(skillId)] || {};
    const yBucket = Math.round(clampPercent(marker.y, 7, 93) * 2) / 2;
    const key = `${skillId}:${marker.side || "attacker"}:${yBucket}`;
    if (seen.has(key)) return;
    seen.add(key);
    const label = String(catalogEntry?.officerLabel || catalogEntry?.label || `Officer Skill ${skillId}`);
    markers.push({
      key: `${key}:${index}`,
      x: clampPercent(marker.x, 2, 10),
      y: yBucket,
      side: String(marker.side || "attacker") === "defender" ? "defender" : "attacker",
      skillId,
      label,
      skillLabel: String(catalogEntry?.label || `Skill ${skillId}`),
      iconPath: String(catalogEntry?.officerIconPath || catalogEntry?.iconPath || ""),
      fallbackText: label.trim().charAt(0).toUpperCase() || "?",
    });
  });
  return markers;
};

const BattlefieldFormation = ({
  config,
  attackEvents = [],
  attackerStatCardMap = {},
  defenderStatCardMap = {},
  showHoverStats = false,
  showDebugOverlay = false,
  stackStates = {},
  debugEvents = [],
  targetLines = [],
  movementVectors = [],
  castMarkers = [],
  areaEffects = [],
  traceRenderStacks = [],
  frameActionLog = [],
  troopDisplayByGid = {},
  skillCatalogById = {},
  onToggleTracePlayback,
  onToggleFrameActionPanel,
  onStepTracePlayback,
  onStepTracePlaybackBackward,
  onSeekTracePlayback,
  onChangeTracePlaybackSpeed,
  onChangeProjectionMode,
  onReset,
  onRunServerParity,
  isServerSimulating = false,
  isTracePlaying = false,
  tracePlaybackFrameIndex = 0,
  tracePlaybackFrameCount = 0,
  tracePlaybackSpeed = 1,
  tracePlaybackBaseFps = 50,
  traceProjectionMode = BATTLE_PROJECTION_MODES.GAME,
  showFrameActionPanel = true,
  traceMeta = null,
  serverParitySummary = "",
  serverParityError = "",
  runServerParityLabel = "Run Server Parity",
  runningServerParityLabel = "Running Server Parity...",
}) => {
  const troopDefinitions = config?.troopDefinitions || [];
  const shellRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const troopById = new Map(troopDefinitions.map((troop) => [String(troop.id), troop]));

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === shellRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const handleToggleFullscreen = useCallback(async () => {
    const node = shellRef.current;
    if (!node) return;
    try {
      if (document.fullscreenElement === node) {
        await document.exitFullscreen();
        return;
      }
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      await node.requestFullscreen();
    } catch (error) {
      setIsFullscreen((value) => !value);
    }
  }, []);

  const normalizedTraceStacks = (Array.isArray(traceRenderStacks) ? traceRenderStacks : [])
    .map((stack, index) => {
      const troopId = String(stack?.troopId || "");
      const rawTroopId = String(stack?.rawTroopId || "");
      const summonType = String(stack?.summonType || "").trim();
      const isWarplaneRobotSummon = summonType === "warplane_robot"
        || rawTroopId === "33620629"
        || String(stack?.stackId || "").startsWith("summon:");
      const side = String(stack?.side || "attacker") === "defender" ? "defender" : "attacker";
      const troop = troopById.get(troopId) || {
        id: isWarplaneRobotSummon ? `warplane_robot_${index + 1}` : (troopId || `unknown_${index + 1}`),
        label: isWarplaneRobotSummon ? "Mechanical Golem" : (troopId || `Unknown ${index + 1}`),
        row: "mid",
        iconPath: isWarplaneRobotSummon ? "/battle-sim-icons/warplane.png" : "",
      };
      const stackId = String(stack?.stackId || `${side}:${troop.id}:${index}`);
      const mapKey = `${side}:${troop.id}:${stackId}`;
      const visualSlots = Array.isArray(stack?.visualSlots)
        ? stack.visualSlots
          .map((slot, slotIndex) => ({
            key: String(slot?.key || `${stackId}:slot:${slotIndex}`),
            x: clampPercent(Number(slot?.x ?? 50), 1, 99),
            y: clampPercent(Number(slot?.y ?? 50), 1, 99),
            index: Number(slot?.index ?? slotIndex),
          }))
          .filter((slot) => Number.isFinite(slot.x) && Number.isFinite(slot.y))
        : [];
      return {
        key: String(stack?.key || mapKey),
        mapKey,
        stackId,
        troop,
        count: Math.max(0, Math.floor(Number(stack?.count || 0))),
        x: clampPercent(Number(stack?.x ?? 50), 1, 99),
        y: clampPercent(Number(stack?.y ?? 50), 1, 99),
        side,
        visualSlots,
        labels: {
          logicalCount: String(stack?.labels?.logicalCount || ""),
          visualProxyCount: Number(stack?.labels?.visualProxyCount || visualSlots.length || 0),
        },
        startup: {
          visible: Boolean(stack?.startup?.visible ?? true),
          revealProgress: Number(stack?.startup?.revealProgress ?? 1),
          justRevealed: Boolean(stack?.startup?.justRevealed),
        },
      };
    });
  const hasTraceRenderData = normalizedTraceStacks.length > 0;
  const visibleTraceStacks = normalizedTraceStacks.filter((stack) => stack.startup.visible !== false);
  const attackerStacks = hasTraceRenderData
    ? visibleTraceStacks.filter((stack) => stack.side === "attacker")
    : [];
  const defenderStacks = hasTraceRenderData
    ? visibleTraceStacks.filter((stack) => stack.side === "defender")
    : [];

  const allStacks = [...defenderStacks, ...attackerStacks];
  const stackMap = new Map();
  allStacks.forEach((stack) => {
    stackMap.set(stack.mapKey, stack);
    const aliasKey = `${stack.side}:${stack.troop.id}`;
    if (!stackMap.has(aliasKey)) {
      stackMap.set(aliasKey, stack);
    }
  });
  const stackMapById = new Map(
    allStacks
      .map((stack) => [String(stack?.stackId || ""), stack])
      .filter(([stackId]) => Boolean(stackId)),
  );
  const officerSourceMarkers = resolveOfficerSourceMarkers(attackEvents, skillCatalogById);
  const resolveOverlayOpacity = (entry, fallback = 1) => {
    const value = Number(entry?.opacity);
    if (!Number.isFinite(value)) return fallback;
    return Math.max(0.05, Math.min(1, value));
  };

  return (
    <div ref={shellRef} className={`battlefield-shell ${isFullscreen ? "is-fullscreen" : ""}`}>
      <div className={`battlefield-stage ${showFrameActionPanel ? "with-frame-actions" : "without-frame-actions"}`}>
        <FrameActionPanel
          visible={showFrameActionPanel}
          frameActionLog={frameActionLog}
          troopById={troopById}
          troopDisplayByGid={troopDisplayByGid}
          skillCatalogById={skillCatalogById}
        />
        <div className="battlefield-canvas-wrap">
          <div className="battlefield-canvas">
        <div className="battlefield-grid-overlay" />
        <div className="battlefield-divider" />
        <div className="battlefield-side-label defender">Defender</div>
        <div className="battlefield-side-label attacker">Attacker</div>

        <svg className="battlefield-attacks-overlay" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <marker id="attack-arrow-red" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#cf3a3a" />
            </marker>
            <marker id="attack-arrow-blue" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#2f6ea6" />
            </marker>
            <marker id="attack-arrow-skill" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#f59e0b" />
            </marker>
            <marker id="attack-arrow-move-attacker" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="3.5" markerHeight="3.5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#16a34a" />
            </marker>
            <marker id="attack-arrow-move-defender" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="3.5" markerHeight="3.5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#0ea5e9" />
            </marker>
            <clipPath id="battle-officer-source-clip">
              <circle cx="0" cy="0" r="2.75" />
            </clipPath>
          </defs>

          {attackEvents.map((event, index) => {
            const explicitX1 = Number(event?.x1);
            const explicitY1 = Number(event?.y1);
            const explicitX2 = Number(event?.x2);
            const explicitY2 = Number(event?.y2);
            const hasExplicitLine = (
              Number.isFinite(explicitX1)
              && Number.isFinite(explicitY1)
              && Number.isFinite(explicitX2)
              && Number.isFinite(explicitY2)
            );
            const from = hasExplicitLine
              ? { x: explicitX1, y: explicitY1 }
              : ((event?.fromStackId ? stackMapById.get(String(event.fromStackId)) : null)
                || stackMap.get(`${event.fromSide}:${event.fromTroopId}`));
            const to = hasExplicitLine
              ? { x: explicitX2, y: explicitY2 }
              : ((event?.toStackId ? stackMapById.get(String(event.toStackId)) : null)
                || stackMap.get(`${event.toSide}:${event.toTroopId}`));
            if (!from || !to) return null;

            const isDefenderAttack = event.fromSide === "defender";
            return (
              <line
                key={`${event.persistedFromFrame ?? "live"}-${event.persistedIndex ?? index}-${event.fromSide}-${event.fromTroopId}-${event.toSide}-${event.toTroopId}-${index}`}
                className={`battle-attack-line ${isDefenderAttack ? "defender" : "attacker"}`}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                markerEnd={`url(#${isDefenderAttack ? "attack-arrow-blue" : "attack-arrow-red"})`}
                style={{ opacity: resolveOverlayOpacity(event, 0.85) }}
              />
            );
          })}
          {targetLines.map((line, index) => (
            <line
              key={`target-line-${line?.persistedFromFrame ?? "live"}-${line?.persistedIndex ?? index}-${index}`}
              className={`battle-target-line ${line?.style === "skill" ? "skill" : "attack"}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              markerEnd={`url(#${line?.style === "skill" ? "attack-arrow-skill" : "attack-arrow-red"})`}
              style={{ opacity: resolveOverlayOpacity(line, 0.58) }}
            />
          ))}
          {movementVectors.map((line, index) => (
            <line
              key={`move-line-${line?.persistedFromFrame ?? "live"}-${line?.persistedIndex ?? index}-${index}`}
              className={`battle-move-line ${line?.side === "defender" ? "defender" : "attacker"}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              markerEnd={`url(#${line?.side === "defender" ? "attack-arrow-move-defender" : "attack-arrow-move-attacker"})`}
              style={{ opacity: resolveOverlayOpacity(line, 0.72) }}
            />
          ))}
          {officerSourceMarkers.map((marker) => (
            <g
              key={marker.key}
              className={`battle-officer-source-marker ${marker.side}`}
              transform={`translate(${marker.x} ${marker.y})`}
            >
              <title>{`${marker.label}: ${marker.skillLabel}`}</title>
              <circle className="battle-officer-source-marker-glow" cx="0" cy="0" r="3.65" />
              <circle className="battle-officer-source-marker-ring" cx="0" cy="0" r="3" />
              {marker.iconPath ? (
                <image
                  className="battle-officer-source-marker-image"
                  href={marker.iconPath}
                  x="-2.75"
                  y="-2.75"
                  width="5.5"
                  height="5.5"
                  preserveAspectRatio="xMidYMid slice"
                  clipPath="url(#battle-officer-source-clip)"
                />
              ) : (
                <text className="battle-officer-source-marker-fallback" x="0" y="0">
                  {marker.fallbackText}
                </text>
              )}
            </g>
          ))}
          {areaEffects.map((effect, index) => (
            <circle
              key={`area-effect-${index}`}
              className={`battle-area-effect ${effect?.side === "defender" ? "defender" : "attacker"} ${String(effect?.kind || "").replace(/[^a-z0-9_-]/gi, "").toLowerCase()}`.trim()}
              cx={effect.x}
              cy={effect.y}
              r={Number(effect?.radius || 8)}
            >
              {effect?.label ? <title>{String(effect.label)}</title> : null}
            </circle>
          ))}
          {castMarkers.map((marker, index) => (
            <circle
              key={`cast-marker-${index}`}
              className={`battle-cast-marker ${marker?.side === "defender" ? "defender" : "attacker"} ${String(marker?.kind || "").replace(/[^a-z0-9_-]/gi, "").toLowerCase()}`.trim()}
              cx={marker.x}
              cy={marker.y}
              r={Number(marker?.radius || 1.1)}
            >
              {marker?.label ? <title>{String(marker.label)}</title> : null}
            </circle>
          ))}
        </svg>

        {hasTraceRenderData && showDebugOverlay ? (
          <div className="battle-stack-slots-overlay">
            {[...defenderStacks, ...attackerStacks].flatMap((stack) => (
              (Array.isArray(stack.visualSlots) ? stack.visualSlots : []).map((slot) => (
                <span
                  key={slot.key}
                  className={`battle-stack-slot ${stack.side}`}
                  style={{ left: `${slot.x}%`, top: `${slot.y}%` }}
                  title={`${stack.troop.label} slot ${Number(slot.index) + 1}`}
                />
              ))
            ))}
          </div>
        ) : null}

        {defenderStacks.map((stack) => (
          <BattlefieldStack
            key={stack.key}
            troop={stack.troop}
            count={stack.count}
            x={stack.x}
            y={stack.y}
            side={stack.side}
            statCard={defenderStatCardMap?.[stack.troop.id]}
            showHoverStats={showHoverStats}
            showDebugOverlay={showDebugOverlay}
            stackDebug={(stack?.stackId ? stackStates?.[stack.stackId] : null) || stackStates?.[`${stack.side}:${stack.troop.id}`]}
            stackId={stack.stackId}
            logicalCountLabel={stack?.labels?.logicalCount}
            visualProxyCount={stack?.labels?.visualProxyCount}
            startupJustRevealed={Boolean(stack?.startup?.justRevealed)}
          />
        ))}

        {attackerStacks.map((stack) => (
          <BattlefieldStack
            key={stack.key}
            troop={stack.troop}
            count={stack.count}
            x={stack.x}
            y={stack.y}
            side={stack.side}
            statCard={attackerStatCardMap?.[stack.troop.id]}
            showHoverStats={showHoverStats}
            showDebugOverlay={showDebugOverlay}
            stackDebug={(stack?.stackId ? stackStates?.[stack.stackId] : null) || stackStates?.[`${stack.side}:${stack.troop.id}`]}
            stackId={stack.stackId}
            logicalCountLabel={stack?.labels?.logicalCount}
            visualProxyCount={stack?.labels?.visualProxyCount}
            startupJustRevealed={Boolean(stack?.startup?.justRevealed)}
          />
        ))}
      </div>

      <div className="battlefield-controls-placeholder">
        <>
          <button type="button" onClick={onToggleTracePlayback} disabled={!tracePlaybackFrameCount || isServerSimulating}>
            {isTracePlaying ? "Pause" : "Play"}
          </button>
          <button type="button" onClick={onStepTracePlaybackBackward} disabled={!tracePlaybackFrameCount || isServerSimulating}>
            Step Back
          </button>
          <button type="button" onClick={onStepTracePlayback} disabled={!tracePlaybackFrameCount || isServerSimulating}>
            Step Frame
          </button>
          <button
            type="button"
            onClick={() => onToggleFrameActionPanel && onToggleFrameActionPanel()}
            disabled={isServerSimulating}
          >
            {showFrameActionPanel ? "Hide Actions" : "Show Actions"}
          </button>
          <button type="button" onClick={handleToggleFullscreen}>
            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          </button>
          <label className="battle-control-field">
            View
            <select
              value={traceProjectionMode}
              onChange={(event) => onChangeProjectionMode && onChangeProjectionMode(event.target.value)}
              disabled={!tracePlaybackFrameCount || isServerSimulating}
            >
              <option value={BATTLE_PROJECTION_MODES.GAME}>Game</option>
              <option value={BATTLE_PROJECTION_MODES.TACTICAL}>Tactical</option>
            </select>
          </label>
          <label className="battle-control-field">
            Speed
            <select
              value={String(tracePlaybackSpeed)}
              onChange={(event) => onChangeTracePlaybackSpeed && onChangeTracePlaybackSpeed(Number(event.target.value))}
              disabled={!tracePlaybackFrameCount || isServerSimulating}
            >
              <option value="0.25">0.25x</option>
              <option value="0.5">0.5x</option>
              <option value="1">1x</option>
              <option value="2">2x</option>
              <option value="4">4x</option>
            </select>
          </label>
        </>
        <button type="button" onClick={onReset}>
          Reset
        </button>
        <button type="button" onClick={onRunServerParity} disabled={isServerSimulating}>
          {isServerSimulating ? runningServerParityLabel : runServerParityLabel}
        </button>
      </div>
      <div className="battle-server-summary">
        {tracePlaybackFrameCount
          ? `Trace Frame ${Math.min(tracePlaybackFrameCount, Math.max(1, Number(tracePlaybackFrameIndex) + 1)).toLocaleString()} / ${Number(tracePlaybackFrameCount).toLocaleString()} | ${traceProjectionMode === BATTLE_PROJECTION_MODES.GAME ? "Game View" : "Tactical View"} | ${Number(tracePlaybackSpeed || 1)}x (${Number((Number(tracePlaybackBaseFps || 50) * Number(tracePlaybackSpeed || 1)).toFixed(1))} fps)`
          : "Trace-only renderer. Run Server Parity to load backend frame trace."}
      </div>
      {tracePlaybackFrameCount ? (
        <input
          className="battle-trace-slider"
          type="range"
          min={0}
          max={Math.max(0, tracePlaybackFrameCount - 1)}
          value={Math.min(Math.max(0, Number(tracePlaybackFrameIndex || 0)), Math.max(0, tracePlaybackFrameCount - 1))}
          onChange={(event) => onSeekTracePlayback && onSeekTracePlayback(Number(event.target.value))}
        />
      ) : null}
      {traceMeta?.projectionPresetId ? (
        <div className="battle-server-summary">
          Projection Preset: {traceMeta.projectionPresetId}
        </div>
      ) : null}
      {serverParitySummary ? <div className="battle-server-summary">{serverParitySummary}</div> : null}
      {serverParityError ? <div className="battle-server-error">{serverParityError}</div> : null}
      {showDebugOverlay ? (
        <div className="battle-debug-panel">
          <div className="battle-debug-title">Recent Tick Events</div>
          <ul>
            {(debugEvents || []).slice(-12).reverse().map((event, idx) => (
              <li key={`${event.type}-${event.side}-${event.troopId}-${idx}`}>
                [{event.type}] {event.side}:{event.troopId}
                {event.toTargetId ? ` -> ${event.toTargetId}` : ""}
                {event.targetId ? ` -> ${event.targetId}` : ""}
                {Number.isFinite(Number(event.damage)) ? ` dmg:${Number(event.damage).toLocaleString()}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
        </div>
      </div>
    </div>
  );
};

export default BattlefieldFormation;
